import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  DsBoxModule,
  DsButtonModule,
  DsIconModule,
  DsMessageModule,
  DsProgressBarModule,
  DsTagModule,
} from '@bmw-ds/components';

import { ConsentReadonlyComponent } from '../../../consent/components/consent-readonly/consent-readonly.component';
import { UseConsent } from '../../../consent/composables/consent.composable';
import { ConsentType } from '../../../consent/model/consent-checkbox';
import { Customer } from '../../../customer/model/customer.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../../loading/loading-indicator.component';
import { VehiclesOverviewComponent } from '../../../vehicle/components/vehicles-overview/vehicles-overview.component';
import { UseCustomerInsights } from '../customer-insights/composables/customer-insights.composable';
import { NextBestVehicleComponent } from '../customer-insights/next-best-vehicle/next-best-vehicle.component';
import { ProfilePageTabId } from '../profile.page';

@Component({
  selector: 'cdf-profile-tab-overview',
  imports: [
    DsBoxModule,
    DsIconModule,
    DsTagModule,
    VehiclesOverviewComponent,
    DsProgressBarModule,
    NextBestVehicleComponent,
    ConsentReadonlyComponent,
    LoadingIndicatorComponent,
    DsMessageModule,
    DsButtonModule,
  ],
  template: `
    <div class="grid">
      <ds-box
        [class.tile-click-target]="!enableVehicleSelection()"
        (click)="!enableVehicleSelection() && openTab.emit('vehicles')"
        elevation="base"
        [class.grid-column-span-full]="!customerInsights().enabled()"
      >
        <ds-box-content>
          <header
            [class.tile-click-target]="enableVehicleSelection()"
            (click)="enableVehicleSelection() && openTab.emit('vehicles')"
          >
            <h2>{{ i18n('vehicles.heading') }}</h2>
            <ds-icon icon="external_link" />
          </header>
          <vehicles-overview
            [vehicles]="customer().vehicles"
            [activeCountry]="activeCountry()"
            [enableVehicleSelection]="enableVehicleSelection()"
            (selectVehicle)="selectVehicle.emit($event)"
            (plusXCardClick)="openTab.emit('vehicles')"
          />
        </ds-box-content>
      </ds-box>

      @if (customerInsights().enabled()) {
        <ds-box class="tile-click-target" (click)="openTab.emit('customerInsights')" elevation="base">
          <ds-box-content>
            <header>
              <h2>
                {{ i18n('customerInsights.header') }}
                @if (
                  !customerInsights().loading() &&
                  customerInsights().brandSelection() &&
                  customerInsights().insightViewModel() !== undefined
                ) {
                  <ds-tag tone="neutral">{{ customerInsights().brandSelection() }}</ds-tag>
                }
              </h2>
              <ds-icon icon="external_link" />
            </header>

            @if (customerInsights().loading()) {
              <cdf-loading-indicator />
            } @else if (customerInsights().insightViewModel() === undefined) {
              <ds-message
                [messageText]="i18n('customerInsights.noInsightsAvailable')"
                [isDismissable]="false"
                tone="info"
                variant="slim"
              />
            } @else {
              <div class="insights-grid">
                @if (customerInsights().insightViewModel()?.customerValueClass) {
                  <span>{{ i18n('customerInsights.customerValue') }}</span>
                  <ds-progress-bar
                    variant="determinate"
                    [progress]="((customerInsights().insightViewModel()?.customerValueClass ?? 0) / 5) * 100"
                  />
                }
                @if (customerInsights().insightViewModel()?.purchaseScoreClass) {
                  <span>{{ i18n('customerInsights.purchaseScore') }}</span>
                  <ds-progress-bar
                    variant="determinate"
                    [progress]="((customerInsights().insightViewModel()?.purchaseScoreClass ?? 0) / 20) * 100"
                  />
                }
                @if (customerInsights().insightViewModel()?.bevScoreClass) {
                  <span>{{ i18n('customerInsights.bevScoreClass') }}</span>
                  <ds-progress-bar
                    variant="determinate"
                    [progress]="((customerInsights().insightViewModel()?.bevScoreClass ?? 0) / 20) * 100"
                  />
                }
                @if (customerInsights().insightViewModel()?.nextBestVehicles) {
                  <span>{{ i18n('customerInsights.nextBestVehicle') }}</span>
                  <cdf-next-best-vehicle
                    [vehicles]="customerInsights().insightViewModel()?.nextBestVehicles"
                    maxVehicles="1"
                    imageOnly
                    imageSmall
                  />
                }
              </div>
            }
          </ds-box-content>
        </ds-box>
      }

      <ds-box
        class="tile-click-target"
        (click)="openTab.emit('consents')"
        elevation="base"
        [class.grid-column-span-full]="!customerInsights().enabled() && !consent().dealer.showConsent()"
      >
        <ds-box-content>
          <header>
            <h2>{{ i18n('consent.nscHeader') }}</h2>
            <ds-icon icon="external_link" />
          </header>
          @if (!customer().gcid) {
            <ds-message tone="caution" variant="slim" [messageText]="missingRegistrationMessage()" />
          } @else {
            <cdf-consent-readonly
              [consentCheckboxGroups]="consent().nsc.consentCheckboxes()"
              [consentSelection]="consent().nsc.consentSelection()"
              [infoMessageText]="consent().nsc.noConsentAvailable() ? i18n('consent.initialConsentCreationMode') : ''"
            />
          }
        </ds-box-content>
      </ds-box>

      @if (consent().dealer.showConsent()) {
        <ds-box class="tile-click-target" (click)="openTab.emit('consents')" elevation="base">
          <ds-box-content>
            <header>
              <h2>{{ i18n('consent.dealerHeader') }}</h2>
              <ds-icon icon="external_link" />
            </header>
            <cdf-consent-readonly
              [consentCheckboxGroups]="consent().dealer.consentCheckboxes()"
              [consentSelection]="consent().dealer.consentSelection()"
              [infoMessageText]="
                consent().dealer.zeroConsentsAvailable() ? i18n('consent.initialConsentCreationMode') : ''
              "
            />
          </ds-box-content>
        </ds-box>
      }
    </div>
  `,
  styleUrl: './tab-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabOverviewComponent {
  protected readonly i18n = useI18n();

  readonly customerInsights = input.required<UseCustomerInsights>();
  readonly consent = input.required<UseConsent>();
  readonly customer = input.required<Customer>();
  readonly activeCountry = input<string | undefined>();

  readonly enableVehicleSelection = input(false, { transform: booleanAttribute });

  readonly openTab = output<ProfilePageTabId>();
  readonly selectVehicle = output<string>();

  protected readonly missingRegistrationMessage = computed(() =>
    this.i18n('consent.missingRegistration', {
      consentType: ConsentType.NSC,
    }),
  );
}
